import React from "react";
import { keyframes } from "@emotion/react";
import { Box, Typography } from "@mui/joy";

const INK = "#1a1a2e";
const HUD_FONT = "'Chakra Petch', 'Poppins', sans-serif";

const tileHop = keyframes`
  0%, 100% { transform: translateY(0) rotate(var(--tilt)); }
  40% { transform: translateY(-7px) rotate(0deg); }
`;

// Each tile is [letter, text color, background color, rotation in degrees].
// Words are grouped so a title only ever wraps between words.
const projects = [
  {
    title: "Moconnect",
    type: "E-commerce",
    accent: "#a13030",
    image: "/Moconnect_Logo.jpeg",
    alt: "Moconnect project by Mary Ann Wambui",
    words: [
      [
        ["M", "#00b4d8", "#D2B48C", -5],
        ["O", "black", "#D2B48C", 3],
        ["C", "black", "#FE7743", -8],
        ["O", "#000000", "#FE7743", 5],
        ["N", "#D2B48C", "black", -3],
        ["N", "#C5172E", "#D2B48C", 3],
        ["E", "whitesmoke", "#504C57", -8],
        ["C", "#BB3E00", "#D2B48C", 5],
        ["T", "#00b4d8", "#D2B48C", -3],
      ],
    ],
    description:
      "An E-commerce platform built with React and Django, featuring real-time inventory management and secure payment integration.",
    tech: ["React.js", "Django", "PostgreSQL", "AWS", "Material-UI"],
    link: "https://www.moconnect.co/",
  },
  {
    title: "Salon Hub",
    type: "Web App",
    accent: "#541e6b",
    image: "/hair_profile.png",
    alt: "Salon Hub salon rating platform built with ASP.NET",
    words: [
      [
        ["S", "#00b4d8", "#D2B48C", -5],
        ["A", "black", "#D2B48C", 3],
        ["L", "black", "#FE7743", -8],
        ["O", "#000000", "#FE7743", 5],
        ["N", "#D2B48C", "black", -3],
      ],
      [
        ["H", "#C5172E", "#D2B48C", 3],
        ["U", "whitesmoke", "#504C57", -8],
        ["B", "#BB3E00", "#D2B48C", 5],
      ],
    ],
    description:
      "A comprehensive platform designed to enhance customer experience in the salon industry through real-time feedback and ratings.",
    tech: ["ASP.NET", "C#", "SQL Server", "BootStrap"],
    link: "https://destyle.runasp.net/",
  },
  {
    title: "Qui Mobile",
    type: "Mobile App",
    accent: "#1e2b54",
    image: "/Majiapp.png",
    imageFit: "contain",
    alt: "Maji mobile app project",
    words: [
      [
        ["Q", "#00b4d8", "#D2B48C", -5],
        ["I", "black", "#D2B48C", 3],
        ["U", "black", "#FE7743", -8],
      ],
      [
        ["M", "#000000", "#FE7743", 5],
        ["O", "#D2B48C", "black", -3],
        ["B", "#C5172E", "#D2B48C", 3],
        ["I", "whitesmoke", "#504C57", -8],
        ["L", "#BB3E00", "#D2B48C", 5],
        ["E", "#00b4d8", "#D2B48C", -3],
      ],
    ],
    description:
      "Qiu mobile app is a marketplace where water suppliers and customers can be able to interact and customer's using their location to get suppliers near them.",
    tech: ["React Native", "Material-UI", "JavaScript", "Expo", "Figma"],
  },
  {
    title: "Sandstones",
    type: "UX/UI Design",
    accent: "#b4501f",
    image: "/Jewelery.png",
    alt: "Sandstones online jewelry store design",
    words: [
      [
        ["S", "#00b4d8", "#D2B48C", -5],
        ["A", "black", "#D2B48C", 3],
        ["N", "black", "#FE7743", -8],
        ["D", "#000000", "#FE7743", 5],
        ["S", "#D2B48C", "black", -3],
        ["T", "#C5172E", "#D2B48C", 3],
        ["O", "whitesmoke", "#504C57", -8],
        ["N", "#BB3E00", "#D2B48C", 5],
        ["E", "#00b4d8", "#D2B48C", -3],
        ["S", "black", "#FE7743", 4],
      ],
    ],
    description:
      "This is platform that involves selling jewelry online. I designed the blueprint of the website.",
    tech: ["Figma", "Canva", "Sketch", "UX/UI Design"],
  },
  {
    title: "Agri Records",
    type: "Web App",
    accent: "#2f6b4f",
    image: "/agri_tech.jpg",
    alt: "Agri Records crop management app built with React and Django",
    words: [
      [
        ["A", "#00b4d8", "#D2B48C", -5],
        ["G", "black", "#D2B48C", 3],
        ["R", "black", "#FE7743", -8],
        ["I", "#000000", "#FE7743", 5],
      ],
      [
        ["R", "#D2B48C", "black", -3],
        ["E", "#C5172E", "#D2B48C", 3],
        ["C", "whitesmoke", "#504C57", -8],
        ["O", "#BB3E00", "#D2B48C", 5],
        ["R", "#00b4d8", "#D2B48C", -3],
        ["D", "#822424", "#FE7743", 3],
        ["S", "#1e2b54", "#D2B48C", -8],
      ],
    ],
    description:
      "Agri records manages your crops, track performance and growing smarter.",
    tech: ["React.js", "Render", "Superbase", "Django", "PostgreSQL", "Material-UI"],
    link: "https://agtech-beta.vercel.app/",
  },
];

const hudLabel = {
  fontFamily: HUD_FONT,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
};

function TitleTiles({ title, words }) {
  return (
    <Box
      component="h3"
      aria-label={title}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px 12px",
        margin: "0 0 12px",
      }}
    >
      {words.map((word, wordIndex) => (
        <Box
          key={wordIndex}
          component="span"
          aria-hidden="true"
          sx={{ display: "inline-flex", gap: "4px" }}
        >
          {word.map(([letter, color, bgColor, rotation], index) => (
            <Typography
              key={index}
              component="span"
              className="tile"
              sx={{
                "--tilt": `${rotation}deg`,
                animationDelay: `${(wordIndex * 3 + index) * 45}ms`,
                fontSize: { xs: "20px", md: "22px" },
                lineHeight: 1.3,
                fontWeight: "bold",
                color,
                backgroundColor: bgColor,
                padding: "1px 3px",
                borderRadius: "5px",
                boxShadow: "0 2px 0 rgba(26,26,46,0.45)",
                transform: "rotate(var(--tilt))",
                fontFamily:
                  index % 2 ? "Impact, Charcoal, sans-serif" : "Georgia, serif",
                display: "inline-block",
              }}
            >
              {letter}
            </Typography>
          ))}
        </Box>
      ))}
    </Box>
  );
}

function GameScreen({ image, imageFit = "cover", alt }) {
  const corners = [
    { top: 8, left: 8, borderWidth: "3px 0 0 3px" },
    { top: 8, right: 8, borderWidth: "3px 3px 0 0" },
    { bottom: 8, left: 8, borderWidth: "0 0 3px 3px" },
    { bottom: 8, right: 8, borderWidth: "0 3px 3px 0" },
  ];

  return (
    <Box sx={{ padding: "12px 12px 0" }}>
      <Box
        sx={{
          position: "relative",
          height: "190px",
          borderRadius: "10px",
          overflow: "hidden",
          border: `2px solid ${INK}`,
          backgroundColor: imageFit === "contain" ? "#f5f5f5" : INK,
          // Soft scanlines, so screenshots read as a game screen
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "repeating-linear-gradient(0deg, rgba(26,26,46,0.035) 0 1px, transparent 1px 4px)",
          },
          "& img": {
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: imageFit,
          },
        }}
      >
        <img src={image} alt={alt} loading="lazy" />
        {corners.map((corner, index) => (
          <Box
            key={index}
            className="corner"
            aria-hidden="true"
            sx={{
              position: "absolute",
              zIndex: 1,
              width: "18px",
              height: "18px",
              borderStyle: "solid",
              borderColor: "white",
              filter: `drop-shadow(0 0 1px ${INK})`,
              opacity: 0,
              transform: "scale(1.5)",
              transition: "opacity 0.2s ease, transform 0.2s ease",
              ...corner,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

function ProjectAction({ title, link }) {
  if (!link) return null;

  return (
    <Box
      component="a"
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${title} (opens in a new tab)`}
      sx={{
        width: "42px",
        height: "42px",
        background: "linear-gradient(135deg, #822424, #541e6b)",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        mt: "auto",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translate(4px, -4px)",
          boxShadow: "0 6px 18px rgba(84,30,107,0.4)",
        },
        "&:focus-visible": {
          outline: "3px solid #541e6b",
          outlineOffset: "3px",
        },
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M5 12H19M19 12L12 5M19 12L12 19"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
}

function ProjectCard({ project }) {
  const { title, type, accent, image, imageFit, alt, words, description, tech, link } =
    project;

  return (
    <Box
      component="article"
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "#fffaf3",
        borderRadius: "16px",
        border: `2px solid ${INK}`,
        boxShadow: "none",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        "@media (hover: hover)": {
          "&:hover": {
            transform: "translate(-3px, -5px) rotate(-0.6deg)",
            boxShadow: `11px 11px 0 ${accent}`,
          },
          "&:hover .corner": { opacity: 1, transform: "scale(1)" },
          "@media (prefers-reduced-motion: no-preference)": {
            "&:hover .tile": { animation: `${tileHop} 0.45s ease` },
          },
        },
        // Outside the hover query so a tap on a touchscreen shows it too
        "&:active, &:has(a:focus-visible)": {
          boxShadow: `6px 6px 0 ${accent}`,
        },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "&:hover": { transform: "none" },
        },
      }}
    >
      {/* HUD strip */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8px 12px",
          backgroundColor: INK,
        }}
      >
        <Typography
          sx={{
            ...hudLabel,
            color: "white",
            fontSize: "0.8rem",
            lineHeight: 1,
            padding: "5px 8px",
            borderRadius: "6px",
            backgroundColor: accent,
            border: "1px solid rgba(255,255,255,0.35)",
          }}
        >
          {type}
        </Typography>
      </Box>

      <GameScreen image={image} imageFit={imageFit} alt={alt} />

      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          padding: "16px 16px 18px",
        }}
      >
        <TitleTiles title={title} words={words} />

        <Typography
          sx={{
            color: "#55556a",
            fontSize: "0.9rem",
            lineHeight: 1.55,
            marginBottom: "14px",
          }}
        >
          {description}
        </Typography>

        {/* Tech stack, shown as an inventory */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            marginBottom: "18px",
          }}
        >
          {tech.map((item) => (
            <Typography
              key={item}
              sx={{
                color: INK,
                backgroundColor: "white",
                padding: "3px 8px",
                borderRadius: "6px",
                border: "1.5px solid rgba(26,26,46,0.75)",
                boxShadow: `2px 2px 0 ${accent}`,
                fontSize: "0.75rem",
                fontWeight: 600,
              }}
            >
              {item}
            </Typography>
          ))}
        </Box>

        <ProjectAction title={title} link={link} />
      </Box>
    </Box>
  );
}

function ProjectCards() {
  return (
    <Box
      sx={{
        padding: { xs: "20px", md: "40px" },
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr",
          },
          gap: "30px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {projects.map((project) => (
          <ProjectCard key={project.title} project={project} />
        ))}
      </Box>
    </Box>
  );
}

export default ProjectCards;
